import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import type {
  FeedStickerResponse,
  FeedStickerRequest,
} from "@/types/generated/cms.generated";

const STICKERS_KEY = ["feed-stickers"] as const;

// Fetch the full sticker catalog
export function useFeedStickers() {
  return useQuery<FeedStickerResponse[]>({
    queryKey: STICKERS_KEY,
    queryFn: () => apiRequest("GET", "/feed-stickers"),
  });
}

// Create a sticker
export function useCreateFeedSticker() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (data: FeedStickerRequest) =>
      apiRequest<FeedStickerResponse>("POST", "/feed-stickers", data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: STICKERS_KEY });
      toast({
        title: "Sticker created",
        description: "The sticker has been added to the catalog.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description:
          error.message || "Failed to create sticker. Please try again.",
        variant: "destructive",
      });
    },
  });
}

// Update a sticker (also used for activate/deactivate and reorder)
export function useUpdateFeedSticker() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: FeedStickerRequest }) =>
      apiRequest<FeedStickerResponse>("PUT", `/feed-stickers/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: STICKERS_KEY });
      toast({
        title: "Sticker updated",
        description: "The sticker has been updated successfully.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description:
          error.message || "Failed to update sticker. Please try again.",
        variant: "destructive",
      });
    },
  });
}

// Delete a sticker
export function useDeleteFeedSticker() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (id: string) => apiRequest("DELETE", `/feed-stickers/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: STICKERS_KEY });
      toast({
        title: "Sticker deleted",
        description: "The sticker has been removed from the catalog.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description:
          error.message || "Failed to delete sticker. Please try again.",
        variant: "destructive",
      });
    },
  });
}
